import {
  AdType,
  Audiences,
  ConfiguredUrl,
  DayPartings,
  LineItemCreative,
  LineItemCreativeActions,
  LineItemCreativeDisableReasons,
  LineItemTargets,
  LineItemTargetValue,
  Maybe,
  PacingValues,
  Package,
  Scalars,
  UpdateAdTags,
} from '../../../apis/graphql/types';
import { NullableDate } from '../../../components/DateRangeFilter';
import { TimeRowState } from '../../../components/ScheduleCalendar/SchedulesSelect/utils';
import { AdDurationType } from '../../../utils/domain/ads/adDuration';
import { AdFormatType } from '../../../utils/domain/ads/adFormat';
import { AGE, allTargetingAgeNames, GENDER, TARGETING_AGE_NAME } from '../constants/ageTargetings';
import { PlatformTargetingType } from '../constants/platformTargetings';
import { DayPart } from './Content/Blocks/Delivery/types';
import { DAY_PARTINGS_VALUES, PacingOptionValue } from './Content/constants';

export type TargetingType = {
  id: string;
  isExclusion: boolean;
};

export type AgesTargetingType = TargetingType & {
  targetingName: string;
};

export type EditingTag = UpdateAdTags & {
  configuredUrl?: string;
  replacedValues?: Record<string, string>;
};

export type LineItemAdAdTags = {
  __typename?: 'AdTags';
  configuredAdTag: ConfiguredUrl;
  id?: Scalars['ID'];
  name: Scalars['String'];
  type: Scalars['String'];
  url: Scalars['String'];
};

export type LineItemAdType = {
  __typename?: 'AdType';
  adTags: Maybe<Array<Maybe<LineItemAdAdTags>>>;
  creative: Maybe<LineItemCreative>;
  weight: Maybe<Scalars['Float']>;
  disabledReasons: Maybe<LineItemCreativeDisableReasons>;
  actions: Maybe<LineItemCreativeActions>;
};

export type LineItemForm = {
  id: string;
  name: string;
  dateRange: string[];
  budget: number | null;
  startTime: NullableDate;
  endTime: NullableDate;
  calculatedCost: string;
  ages: AgesTargetingType[];
  platform: PlatformTargetingType[];
  dayParting: DayPart[];
  pacingSchedules: PacingValues[] | null;
  pacingType: PacingOptionValue;
  pacingWeight?: string;
  frequency: string;
  frequencyCapUnit: string;
  contentTargetings: TargetingType[];
  locationTargetings: TargetingType[];
  updatedAt?: Maybe<string>;
  updatedBy?: Maybe<string>;
  retargetings: TargetingType[];
  publishers: string | null;
  packageId: string | null;
  selectedPackage: Package | null;
  audiences: Audiences[];
  allowMatureContent: boolean;
  unitCost?: number;
  estimatedDailyImpressions?: number;
  targetImpressions?: number | null;
  status?: string | null;
  zipCodes?: Maybe<Array<Maybe<LineItemTargetValue>>>;
  adDuration: AdDurationType;
  adFormat: AdFormatType;
  ads?: Maybe<Array<Maybe<LineItemAdType>>>;
  editingTag?: EditingTag;
  isPreemptible?: boolean;
  weight: Array<Maybe<number>>;
  hasKervOverride?: boolean;
  dayPartingSelectedTime?: keyof typeof DAY_PARTINGS_VALUES;
  dayPartingSelectedDraft: TimeRowState[] | null;
  hasDnaApplied?: boolean | null;
};

export type LineItem = {
  name: string;
  id: string;
  startTime: string;
  endTime: string;
  status: string | null;
  adStatus: string | null;
  targetImpressions: number | null;
  budget: number | null;
  allowMatureContent?: Maybe<boolean>;
  discountedBudget?: number | null;
  deliveredImpressions?: number | null;
  updatedAt?: Maybe<string>;
  updatedBy?: Maybe<string>;
  targetValues: Maybe<LineItemTargets>;
  dayPartings?: Maybe<Array<Maybe<DayPartings>>>;
  pacingSchedules: Maybe<PacingValues[]>;
  frequencyCap: Maybe<number>;
  frequencyCapUnit: Maybe<string>;
  adDuration: string | null;
  ads: Maybe<Array<Maybe<AdType>>>;
};

export type LineItemFormFieldsNames = keyof LineItemForm;

export interface DeliveryRestrictions {
  isDaypartingAllowedByOrg: boolean;
  isPacingAllowedByOrg: boolean;
}

export interface CreativeStudioOrgAccess {
  isAllowedByOrg: boolean;
  isLoading: boolean;
}

export type AgesAndGenders = {
  ages: AGE[];
  genders: GENDER[];
};

export const isTargetingAgeName = (
  targetingAgeName: string | null | undefined
): targetingAgeName is TARGETING_AGE_NAME => {
  if (targetingAgeName === null || targetingAgeName === undefined) return false;

  return allTargetingAgeNames.includes(targetingAgeName as TARGETING_AGE_NAME);
};
